import type { DemoRefV1 } from './types.js';

const SHA256 = /^[a-f0-9]{64}$/;

function decodedSegments(url: URL): string[] | null {
  try {
    return url.pathname.split('/').slice(1).map(decodeURIComponent);
  } catch {
    return null;
  }
}

/** Validate the self-describing digest path as well as the object fields. */
export function isDemoRefV1(value: unknown): value is DemoRefV1 {
  if (!value || typeof value !== 'object') return false;
  const ref = value as Partial<DemoRefV1>;
  if (ref.schemaVersion !== 1 || typeof ref.creationId !== 'string' || !ref.creationId ||
      !Number.isSafeInteger(ref.version) || (ref.version ?? 0) < 1 ||
      typeof ref.artifactDigest !== 'string' || !SHA256.test(ref.artifactDigest) ||
      typeof ref.url !== 'string') return false;
  try {
    const url = new URL(ref.url);
    const loopback = url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
    const publicStorage = url.protocol === 'https:' && url.hostname === 'storage.googleapis.com';
    if (url.username || url.password || url.search || url.hash || (!publicStorage && !loopback)) return false;
    const parts = decodedSegments(url);
    if (!parts || (publicStorage ? parts.length !== 6 || !parts[0] : parts.length < 5)) return false;
    return parts.at(-5) === ref.creationId && parts.at(-4) === String(ref.version) &&
      parts.at(-3) === 'demo' && parts.at(-2) === ref.artifactDigest && parts.at(-1) === 'index.html';
  } catch {
    return false;
  }
}

export function requireDemoRefV1(value: unknown): DemoRefV1 {
  if (!isDemoRefV1(value)) throw new Error('invalid DemoRefV1');
  return value;
}

/** Reconstruct the active projection without adding another creation column.
 * New active bundle URLs are self-describing; legacy `/web/index.html` links
 * remain playable but truthfully have no digest-backed DemoRef. */
export function activeDemoRef(input: {
  id: string;
  activeVersion?: number;
  activeBundleUrl?: string;
}): DemoRefV1 | undefined {
  const { id: creationId, activeVersion: version, activeBundleUrl: url } = input;
  if (!Number.isSafeInteger(version) || (version ?? 0) < 1 || !url) return undefined;
  try {
    const parsed = new URL(url);
    const parts = decodedSegments(parsed);
    const artifactDigest = parts?.at(-2);
    const ref: DemoRefV1 = {
      schemaVersion: 1,
      creationId,
      version: version!,
      artifactDigest: artifactDigest ?? '',
      url: parsed.href,
    };
    return isDemoRefV1(ref) ? ref : undefined;
  } catch {
    return undefined;
  }
}
